import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Badge, Input, Select, Alert } from '../components/design-system';
import { ArrowLeft, Play, Trash2, ExternalLink, CheckCircle, AlertCircle, Clock, Save, TestTube } from 'lucide-react';

const categoryOptions = [
  { value: 'general', label: 'General' }, { value: 'politics', label: 'Politics' },
  { value: 'business', label: 'Business' }, { value: 'technology', label: 'Technology' },
  { value: 'science', label: 'Science' }, { value: 'health', label: 'Health' },
  { value: 'sports', label: 'Sports' }, { value: 'entertainment', label: 'Entertainment' },
  { value: 'world', label: 'World' }, { value: 'local', label: 'Local' },
];

export const SourceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isEditor, isAdmin } = useAuth();
  const [source, setSource] = useState(null);
  const [recentItems, setRecentItems] = useState([]);
  const [itemCount, setItemCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({});

  useEffect(() => { loadSource(); }, [id]);

  const loadSource = async () => {
    try {
      const res = await api.getSource(id);
      setSource(res.source);
      setRecentItems(res.recentItems || []);
      setItemCount(res.itemCount || 0);
      const config = typeof res.source.config === 'string' ? JSON.parse(res.source.config) : res.source.config || {};
      setForm({
        name: res.source.name, url: res.source.url, category: res.source.category || 'general',
        language: res.source.language || 'en', fetchIntervalMinutes: res.source.fetch_interval_minutes || 15,
        isActive: !!res.source.is_active, reliabilityScore: res.source.reliability_score || 0.5,
        config,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.updateSource(id, form);
      setSuccess('Source updated successfully');
      loadSource();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTestResult({ status: 'testing' });
    try {
      const res = await api.testSource(id);
      setTestResult(res.result);
    } catch (err) { setTestResult({ error: err.message }); }
  };

  const handleFetch = async () => {
    setSuccess(''); setError('');
    try {
      const res = await api.fetchSource(id);
      setSuccess(res.message || `Fetched ${res.result?.total || 0} items, ${res.result?.new || 0} new`);
      loadSource();
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this source and all its collected items?')) return;
    try {
      await api.deleteSource(id);
      navigate('/sources');
    } catch (err) { setError(err.message); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>;
  if (!source) return <div className="text-center py-20 text-gray-500">Source not found</div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/sources')} className="p-2 rounded-lg hover:bg-gray-100">
          <ArrowLeft className="h-4 w-4 text-gray-500" />
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">{source.name}</h2>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge variant={source.type === 'rss' ? 'info' : 'warning'}>{source.type}</Badge>
            <Badge variant={source.is_active ? 'success' : 'default'}>{source.is_active ? 'Active' : 'Inactive'}</Badge>
            <span className="text-xs text-gray-400">{itemCount} items collected</span>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditor && <Button variant="secondary" icon={TestTube} onClick={handleTest} size="sm">Test</Button>}
          {isEditor && <Button variant="secondary" icon={Play} onClick={handleFetch} size="sm">Fetch Now</Button>}
          {isAdmin && <Button variant="danger" icon={Trash2} onClick={handleDelete} size="sm">Delete</Button>}
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess('')}>{success}</Alert>}

      {testResult && (
        <Alert type={testResult.reachable ? 'success' : testResult.status === 'testing' ? 'info' : 'warning'}>
          {testResult.status === 'testing' ? 'Testing connection...' :
           testResult.error ? `Error: ${testResult.error}` :
           `Status: ${testResult.status} | Content-Type: ${testResult.contentType} | ${testResult.isValidFeed ? `Valid feed with ${testResult.itemCount} items` : `Content length: ${testResult.contentLength}`}`}
        </Alert>
      )}

      {/* Status Card */}
      <Card className="!p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-xs text-gray-500">Last Fetched</div>
            <div className="text-sm font-medium">{source.last_fetched_at ? new Date(source.last_fetched_at).toLocaleString() : 'Never'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Last Status</div>
            <div className="flex items-center justify-center gap-1">
              {source.last_status === 'success' ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> :
               source.last_status === 'error' ? <AlertCircle className="h-3.5 w-3.5 text-red-500" /> :
               <Clock className="h-3.5 w-3.5 text-gray-400" />}
              <span className="text-sm font-medium capitalize">{source.last_status || 'Pending'}</span>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Total Items</div>
            <div className="text-sm font-medium">{source.items_fetched_total || 0}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Reliability</div>
            <div className="text-sm font-medium">{Math.round((source.reliability_score || 0) * 100)}%</div>
          </div>
        </div>
        {source.last_error && <div className="mt-3 text-xs text-red-600 bg-red-50 p-2 rounded">Error: {source.last_error}</div>}
      </Card>

      {/* Edit Form */}
      {isEditor && (
        <Card>
          <h3 className="text-base font-semibold text-gray-900 mb-4">Source Configuration</h3>
          <div className="space-y-4">
            <Input label="Name" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input label="URL" value={form.url || ''} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Select label="Category" options={categoryOptions} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <Input label="Language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} />
              <Input label="Interval (min)" type="number" min="5" value={form.fetchIntervalMinutes} onChange={(e) => setForm({ ...form, fetchIntervalMinutes: parseInt(e.target.value) })} />
              <Input label="Reliability (0-1)" type="number" min="0" max="1" step="0.1" value={form.reliabilityScore} onChange={(e) => setForm({ ...form, reliabilityScore: parseFloat(e.target.value) })} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="isActive" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
              <label htmlFor="isActive" className="text-sm text-gray-700">Source is active</label>
            </div>

            {source.type === 'aggregator_rss' && (
              <div className="border border-primary-200 bg-primary-50/30 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Aggregator Settings</h4>
                <p className="text-xs text-gray-500">Fetches RSS for article links, then scrapes each target page. Titles like "Headline (Source / Category)" are parsed automatically.</p>
                <div className="grid grid-cols-3 gap-3">
                  <Input label="Max articles/fetch" type="number" min="1" max="50" value={form.config?.maxArticlesPerFetch || 15}
                    onChange={(e) => setForm({ ...form, config: { ...form.config, maxArticlesPerFetch: parseInt(e.target.value) } })} />
                  <Input label="Scrape delay (ms)" type="number" min="0" step="100" value={form.config?.scrapeDelayMs || 300}
                    onChange={(e) => setForm({ ...form, config: { ...form.config, scrapeDelayMs: parseInt(e.target.value) } })} />
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={form.config?.followRedirects !== false}
                        onChange={(e) => setForm({ ...form, config: { ...form.config, followRedirects: e.target.checked } })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                      Follow redirects
                    </label>
                  </div>
                </div>
              </div>
            )}

            {source.type === 'scrape' && (
              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Scraping Selectors</h4>
                <div className="grid grid-cols-2 gap-3">
                  {['title', 'body', 'date', 'author', 'image'].map(field => (
                    <Input key={field} label={`${field.charAt(0).toUpperCase() + field.slice(1)} Selector`}
                      value={form.config?.selectors?.[field] || ''}
                      onChange={(e) => setForm({
                        ...form, config: { ...form.config, selectors: { ...form.config?.selectors, [field]: e.target.value } }
                      })} />
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button icon={Save} loading={saving} onClick={handleSave}>Save Changes</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Recent Items */}
      <Card>
        <h3 className="text-base font-semibold text-gray-900 mb-4">Recent Items ({itemCount})</h3>
        {recentItems.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No items collected yet.</p>
        ) : (
          <div className="space-y-2">
            {recentItems.map(item => (
              <div key={item.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/feed?item=${item.id}`)}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 truncate">{item.title}</div>
                  <div className="text-xs text-gray-400">{item.published_at ? new Date(item.published_at).toLocaleString() : 'No date'}</div>
                </div>
                <Badge variant={item.status === 'used' ? 'success' : item.status === 'analyzed' ? 'info' : 'default'}>{item.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
